import React, { useEffect } from "react";
import { Box, Typography, IconButton, Divider, useMediaQuery, Button } from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import ReactPlayer from "react-player/lazy";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

// Define the props
interface RightPreviewProps {
  file: {
    id: string;
    fileType: string;
    url: string;
    name: string;
    description: string;
    size: number;
    createdAt: string;
    metadata: {
      websiteurl: string;
    };
  };
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

const RightPreview: React.FC<RightPreviewProps> = ({ file, onClose, onNext, onPrevious }) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down("sm"));

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' && onNext) {
        onNext();
      } else if (e.key === 'ArrowLeft' && onPrevious) {
        onPrevious();
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onNext, onPrevious, onClose]);

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "40%",
        maxWidth: isMobile ? "100%" : "600px",
        position: "fixed",
        top: "64px",
        right: 0,
        height: "100vh",
        zIndex: (theme) => theme.zIndex.drawer + 1,
        padding: 2,
        borderLeft: "1px solid #e0e0e0",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.paper",
        boxShadow: "0px 4px 20px rgba(0,0,0,0.1)",
        overflowY: "auto",
      }}
    >
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 100 }}
        transition={{ duration: 0.5 }}
      >
        {/* Media Content */}
        <Box
          sx={{
            width: "100%",
            marginBottom: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            maxHeight: isMobile ? "auto" : "600px",
            overflow: "hidden",
            bgcolor: "background.default",
            p: "5px",
            marginTop: isMobile ? "40%" : "20px",
          }}
        >
          {file.fileType === "image" && (
            <Box
              sx={{ 
                width: "100%", 
                height: "100%", 
                position: 'relative',
                '&:hover .visit-website-button': {
                  opacity: 1,
                  transform: 'translateY(0)',
                }
              }}
            >
              <img
                src={`https://dprstorage.b-cdn.net${file.url}`}
                alt={file.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
                onContextMenu={(e) => e.preventDefault()}
                draggable={false}
              />
              {file.metadata && file.metadata.websiteurl && (
                <Button
                  className="visit-website-button"
                  variant="contained"
                  color="primary"
                  onClick={() => window.open(file.metadata.websiteurl, "_blank")}
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    right: 16,
                    opacity: 0,
                    transform: 'translateY(10px)',
                    transition: 'all 0.3s ease',
                    zIndex: 2,
                  }}
                >
                  Visit Website
                </Button>
              )}
            </Box>
          )}

          {file.fileType === "video" && (
            <ReactPlayer
              url={`https://dprstorage.b-cdn.net${file.url}`}
              controls
              muted={false}
              width="100%"
              height="600px"
              style={{
                objectFit: "contain",
              }}
              config={{
                file: {
                  attributes: {
                    controlsList: "nodownload",
                  },
                },
              }}
            />
          )}

          {/* Document (PDF) */}
          {file.fileType === "document" && (
            // <Box sx={{ maxHeight: isMobile ? "400px": "600px" , width: "100%", overflowY: "auto", objectFit: "contain" }}>
              <iframe src={`https://dprstorage.b-cdn.net${file.url}`} title={file.name} style={{ width: '100%', height: isMobile ? "1000px" : "600px" , objectFit: 'contain',  borderRadius: "0px" }} />
            // </Box>
          )}
        </Box>

        <Divider sx={{ marginBottom: 2 }} />

        {/* File Details */}
        <Box sx={{ padding: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
            <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: 2 }}>
              {file.fileType === "document" ? file.name : "File Details"}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", marginBottom: 1 }}>
              <strong>Size :</strong> {file.size > 1024 * 1024 ? `${(file.size / (1024 * 1024)).toFixed(2)} MB` : `${(file.size / 1024).toFixed(2)} KB`}
            </Typography>
            {/* <Typography variant="body2" sx={{ color: "text.secondary", marginBottom: 1 }}>
              <strong>Created At :</strong> {new Date(file.createdAt).toLocaleString()}
            </Typography> */}
          </Box>
          <Box sx={{ display: "flex",  alignItems: "flex-start", justifyContent: "flex-start" }}>
            {file.metadata && file.metadata.websiteurl && (
              // <Button variant="contained" color="primary" onClick={() => window.open(file.metadata.websiteurl, "_blank")}>
              // Visit Website
              // </Button>
              <></>
            )}
          </Box>
        </Box>

        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            bgcolor: "secondary.main",
            color: "white",
            "&:hover": {
              bgcolor: "secondary.dark",
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </motion.div>
    </Box>
  );
};

export default RightPreview;
